/**
 * Label resolvers and answer → display text for Surveys.
 *
 * PLAIN TEXT ONLY: every function here returns a string that callers render
 * as a React text node. Survey text (org-authored labels, respondent answers)
 * is never interpreted as markup anywhere (docs/SURVEYS.md "Public-form
 * security").
 */
import type {
  SurveyQuestion,
  SurveyQuestionType,
  SurveyResponseSource,
  SurveyStatus,
  SurveyWebsiteButton,
} from '@releve/shared';
import { formatOrgDay, safeFormatDate } from '@/lib/dateUtils';
import { fileStem } from '@/lib/export/fileStem';

export type SurveyTranslate = (key: string, options?: Record<string, unknown>) => string;

export const surveyTypeLabel = (type: SurveyQuestionType, t: SurveyTranslate) => t(`surveys:types.${type}`);
export const surveyStatusLabel = (status: SurveyStatus, t: SurveyTranslate) => t(`surveys:status.${status}`);
/**
 * Survey status → Badge props, per docs/STYLES.md "General Status": open =
 * success, closed = secondary (inactive), draft = warning (pending). Theme
 * tokens only — dark-aware, no raw palette classes.
 */
export const SURVEY_STATUS_BADGE: Record<SurveyStatus, { variant: 'outline' | 'secondary'; className?: string }> = {
  open: { variant: 'outline', className: 'bg-success/10 text-success border-success/30' },
  closed: { variant: 'secondary' },
  draft: { variant: 'outline', className: 'bg-warning/10 text-warning border-warning/30' },
};
export const surveySourceLabel = (source: SurveyResponseSource, t: SurveyTranslate) => t(`surveys:source.${source}`);

/** The surveys list route, relative to the org base path. */
export const SURVEYS_LIST_PATH = '/resources/people/surveys';

/** A survey's detail route (optionally a tab), relative to the org base path. */
export function surveyDetailPath(code: string, tab?: 'overview' | 'results' | 'responses'): string {
  const base = `${SURVEYS_LIST_PATH}/${encodeURIComponent(code)}`;
  return tab ? `${base}/${tab}` : base;
}

/** The public answer link for a token (`/s/:token`, outside the org routes). */
export function surveyPublicUrl(token: string, origin: string = window.location.origin): string {
  return `${origin}/s/${encodeURIComponent(token)}`;
}

/** A survey code as a file-name stem: anything outside [A-Za-z0-9_-] becomes `-`. */
export function surveyFileStem(code: string): string {
  return fileStem(code, 'survey');
}

/** `SRV-001-responses-2026-10-04` — the Responses download's name: today as the org-zone calendar day. */
export function surveyResponsesFileName(code: string, orgTimeZone: string | null, now: Date = new Date()): string {
  return `${surveyFileStem(code)}-responses-${formatOrgDay(now, orgTimeZone, 'yyyy-MM-dd', '')}`;
}

/** `SRV-001` → `SRV-001-qr.svg`. */
export function surveyQrFileName(code: string): string {
  return `${surveyFileStem(code)}-qr.svg`;
}

function labelOf(items: ReadonlyArray<{ id: string; label: string }>, id: unknown): string | null {
  if (typeof id !== 'string') return null;
  return items.find((item) => item.id === id)?.label ?? null;
}

/**
 * One stored answer as display text (`''` = not answered). Malformed values
 * (wrong type, an option the question no longer has) render as `''` rather
 * than throwing — the Responses grid must render whatever is stored.
 */
export function surveyAnswerText(question: SurveyQuestion, value: unknown): string {
  if (value === undefined || value === null) return '';
  switch (question.type) {
    case 'section':
      return '';
    case 'single_choice':
    case 'dropdown':
      return labelOf(question.options, value) ?? '';
    case 'multi_choice':
      return Array.isArray(value)
        ? value
            .map((id) => labelOf(question.options, id))
            .filter((label): label is string => !!label)
            .join(', ')
        : '';
    case 'scale':
    case 'number':
      return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
    case 'rating':
      return typeof value === 'number' && Number.isFinite(value) ? `${value}/${question.max}` : '';
    case 'date':
      return typeof value === 'string' ? safeFormatDate(value, 'MMM dd, yyyy', '') : '';
    case 'choice_grid':
    case 'checkbox_grid': {
      if (typeof value !== 'object' || Array.isArray(value)) return '';
      const cells = value as Record<string, unknown>;
      const parts: string[] = [];
      for (const row of question.rows) {
        const cell = cells[row.id];
        const picked = (Array.isArray(cell) ? cell : [cell])
          .map((id) => labelOf(question.columns, id))
          .filter((label): label is string => !!label);
        if (picked.length > 0) parts.push(`${row.label}: ${picked.join(', ')}`);
      }
      return parts.join('; ');
    }
    default:
      return typeof value === 'string' ? value : '';
  }
}

/** The line above a survey's title, and whether the editor typed it (`custom`). */
export interface SurveyIntroLine {
  text: string;
  /**
   * The survey's own line — shown EXACTLY as typed. The default line (`custom:
   * false`) is the one that takes the uppercase eyebrow style (`SurveyBrandHeader`).
   */
  custom: boolean;
}

/**
 * The line above a survey's title — THE one place this rule lives (the public
 * page, the staff preview, the QR poster and the builder's placeholder all
 * call it): the survey's own line when set, else "{Project} · {Org}"
 * (`public.fromProject`) when the survey has a project, else
 * "A survey from {Org}" (`public.from`).
 */
export function surveyIntroLine(
  input: { introLine?: string | null; projectName?: string | null; orgName: string },
  t: SurveyTranslate,
): SurveyIntroLine {
  const custom = input.introLine?.trim();
  if (custom) return { text: custom, custom: true };
  const project = input.projectName?.trim();
  if (project) return { text: t('surveys:public.fromProject', { project, org: input.orgName }), custom: false };
  return { text: t('surveys:public.from', { org: input.orgName }), custom: false };
}

/**
 * The website button's words: the editor's own text verbatim, else
 * "Visit {hostname}" (the survey's own link — the REAL destination host) or
 * "Visit {org}" (the org's website). One wording for the public page, the
 * preview and the builder's placeholder.
 */
export function surveyWebsiteButtonText(button: SurveyWebsiteButton, t: SurveyTranslate): string {
  switch (button.kind) {
    case 'custom':
      return button.text;
    case 'host':
      return t('surveys:public.visitHost', { host: button.text });
    case 'org':
      return t('surveys:public.visitOrg', { org: button.text });
  }
}
